import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import UniversalQRGenerator from './components/UniversalQRGenerator';
import QRCodeDirectory from './components/QRCodeDirectory';
import QRAnalytics from './components/QRAnalytics';
import BulkQRGeneration from './components/BulkQRGeneration';

const UniversalQRCodeGeneratorManagement = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isStaff, isTrainer, loading: authLoading, profileLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('generator');
  const [allUsers, setAllUsers] = useState([]);
  const [qrCodes, setQRCodes] = useState([]);
  const [stats, setStats] = useState({
    totalQRCodes: 0,
    activeQRCodes: 0,
    totalScans: 0,
    expiringSoon: 0
  });

  const isStaffMember = isGymOwner || isStaff || isTrainer;

  useEffect(() => {
    if (!authLoading && !profileLoading && (!user || !isStaffMember)) {
      navigate('/member-dashboard');
    }
  }, [user, isStaffMember, authLoading, profileLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && isStaffMember && !profileLoading) {
      loadData();
    }
  }, [currentTenant, isStaffMember, profileLoading]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadAllUsers(),
        loadQRCodes(),
        loadStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      const { data, error } = await supabase?.from('tenant_members')?.select(`
          *,
          user_profiles!tenant_members_user_id_fkey(id, full_name, email, avatar_url)
        `)?.eq('tenant_id', currentTenant?.id)?.eq('is_active', true)?.order('user_profiles(full_name)');

      if (error) throw error;
      setAllUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadQRCodes = async () => {
    try {
      // Load from both member_barcodes and staff_qr_codes
      const [memberQRs, staffQRs] = await Promise.all([
        supabase?.from('member_barcodes')?.select(`
            *,
            user_profiles!member_barcodes_user_id_fkey(full_name, email, avatar_url)
          `)?.eq('tenant_id', currentTenant?.id)?.eq('barcode_type', 'qr_code')?.order('created_at', { ascending: false }),
        supabase?.from('staff_qr_codes')?.select(`
            *,
            user_profiles!staff_qr_codes_user_id_fkey(full_name, email, avatar_url)
          `)?.eq('tenant_id', currentTenant?.id)?.order('generated_at', { ascending: false })
      ]);

      const combinedQRs = [
        ...(memberQRs?.data || [])?.map(qr => ({ ...qr, source: 'member' })),
        ...(staffQRs?.data || [])?.map(qr => ({ ...qr, source: 'staff' }))
      ];

      setQRCodes(combinedQRs);
    } catch (error) {
      console.error('Error loading QR codes:', error);
    }
  };

  const loadStats = async () => {
    try {
      const [memberQRs, staffQRs] = await Promise.all([
        supabase?.from('member_barcodes')?.select('*')?.eq('tenant_id', currentTenant?.id)?.eq('barcode_type', 'qr_code'),
        supabase?.from('staff_qr_codes')?.select('*')?.eq('tenant_id', currentTenant?.id)
      ]);

      const allQRs = [...(memberQRs?.data || []), ...(staffQRs?.data || [])];
      const now = new Date();
      const twentyFourHoursFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);

      const activeQRCodes = allQRs?.filter(qr => {
        if (!qr?.is_active) return false;
        if (!qr?.expires_at) return true;
        return new Date(qr?.expires_at) > now;
      })?.length || 0;

      const expiringSoon = allQRs?.filter(qr => {
        if (!qr?.is_active || !qr?.expires_at) return false;
        const expiryDate = new Date(qr?.expires_at);
        return expiryDate > now && expiryDate <= twentyFourHoursFromNow;
      })?.length || 0;

      const totalScans = allQRs?.reduce((sum, qr) => sum + (qr?.scan_count || 0), 0);

      setStats({
        totalQRCodes: allQRs?.length || 0,
        activeQRCodes,
        totalScans,
        expiringSoon
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="QrCode" size={32} className="text-primary" />
            <h1 className="text-3xl font-bold text-foreground">Universal QR Code Generator & Management</h1>
          </div>
          <p className="text-muted-foreground">
            Comprehensive QR code creation and administration for all gym personnel
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-lg shadow-md p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="QrCode" size={20} className="text-blue-500" />
              <span className="text-2xl font-bold text-foreground">{stats?.totalQRCodes}</span>
            </div>
            <p className="text-sm text-muted-foreground">Total QR Codes</p>
          </div>
          <div className="bg-card rounded-lg shadow-md p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="CheckCircle" size={20} className="text-green-500" />
              <span className="text-2xl font-bold text-foreground">{stats?.activeQRCodes}</span>
            </div>
            <p className="text-sm text-muted-foreground">Active QR Codes</p>
          </div>
          <div className="bg-card rounded-lg shadow-md p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="ScanLine" size={20} className="text-purple-500" />
              <span className="text-2xl font-bold text-foreground">{stats?.totalScans}</span>
            </div>
            <p className="text-sm text-muted-foreground">Total Scans</p>
          </div>
          <div className="bg-card rounded-lg shadow-md p-4 border border-border">
            <div className="flex items-center justify-between mb-2">
              <Icon name="Clock" size={20} className="text-orange-500" />
              <span className="text-2xl font-bold text-foreground">{stats?.expiringSoon}</span>
            </div>
            <p className="text-sm text-muted-foreground">Expiring Soon</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <Button
            onClick={() => setActiveTab('generator')}
            variant={activeTab === 'generator' ? 'default' : 'outline'}
            className="flex items-center gap-2"
          >
            <Icon name="Plus" size={18} />
            Generate QR Code
          </Button>
          <Button
            onClick={() => setActiveTab('directory')}
            variant={activeTab === 'directory' ? 'default' : 'outline'}
            className="flex items-center gap-2"
          >
            <Icon name="Users" size={18} />
            User Directory
          </Button>
          <Button
            onClick={() => setActiveTab('bulk')}
            variant={activeTab === 'bulk' ? 'default' : 'outline'}
            className="flex items-center gap-2"
          >
            <Icon name="Layers" size={18} />
            Bulk Generation
          </Button>
          <Button
            onClick={() => setActiveTab('analytics')}
            variant={activeTab === 'analytics' ? 'default' : 'outline'}
            className="flex items-center gap-2"
          >
            <Icon name="BarChart3" size={18} />
            Analytics
          </Button>
        </div>

        {/* Tab Content */}
        {activeTab === 'generator' && (
          <UniversalQRGenerator
            allUsers={allUsers}
            onQRGenerated={loadData}
          />
        )}

        {activeTab === 'directory' && (
          <QRCodeDirectory
            allUsers={allUsers}
            qrCodes={qrCodes}
            onUpdate={loadData}
          />
        )}

        {activeTab === 'bulk' && (
          <BulkQRGeneration
            allUsers={allUsers}
            onBulkGenerated={loadData}
          />
        )}

        {activeTab === 'analytics' && (
          <QRAnalytics qrCodes={qrCodes} />
        )}
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default UniversalQRCodeGeneratorManagement;